import type { Inbox, InboxApproval, InboxQuestion, WorkItem } from '@deskworks/shared-types';
import { START_WORK_APPROVAL } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { AlertTriangle, Bot, CheckCheck, Check, Hand, MessageCircleQuestion, RotateCcw, ShieldAlert, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { AnswerBox } from '@/components/answer-box';
import { DecisionOptions, QuestionOptions } from '@/components/decision-options';
import { Markdown } from '@/components/markdown';
import { Page, PageHeader } from '@/components/page';
import { isPersonTask, PersonTaskCard } from '@/components/person-task-card';
import { RelativeTime } from '@/components/relative-time';
import { EmptyState, ErrorState, LoadingPage, LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { isEmptyObject } from '@/lib/format';
import { findAccount, useInbox, useMe } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useToast } from '@/lib/toast';
import { LiveWorkspaces } from '@/components/live-workspaces';
import { ActionGroupCard, groupActions } from '@/components/actions/actions';

type Section = 'questions' | 'approvals' | 'actions' | 'failed';

/** Removes an entry from the cached inbox right away (the server catches up via realtime/refetch). */
function useOptimisticRemove(accountId: string) {
  const queryClient = useQueryClient();
  return (section: Section, id: string) => {
    queryClient.setQueryData<Inbox>(qk.accountInbox(accountId), (old) => {
      if (!old) return old;
      const next: Inbox = {
        questions: section === 'questions' ? old.questions.filter((q) => q.workItem.id !== id) : old.questions,
        approvals: section === 'approvals' ? old.approvals.filter((a) => a.id !== id) : old.approvals,
        // `id` of an action group is its groupId.
        actions: section === 'actions' ? old.actions.filter((a) => a.groupId !== id) : old.actions,
        failed: section === 'failed' ? old.failed.filter((w) => w.id !== id) : old.failed,
        total: old.total,
      };
      next.total = next.questions.length + next.approvals.length + groupActions(next.actions).length + next.failed.length;
      return next;
    });
    // Let the server confirm a bit later (the agent needs a moment to wake up).
    window.setTimeout(() => void queryClient.invalidateQueries({ queryKey: qk.accountInbox(accountId) }), 4000);
  };
}

/** `/$account/inbox` — everything that needs the user: agent questions, approvals, failed work. */
export function InboxPage() {
  const { account: param } = useParams({ from: '/_authed/$account/inbox' });
  const me = useMe();
  const account = me.data ? findAccount(me.data.accounts, param) : undefined;
  const inbox = useInbox(account?.id);
  const remove = useOptimisticRemove(account?.id ?? '');

  if (!account) return <LoadingPage />;
  const data = inbox.data;
  // What only a person can do comes first: the desk can't do it, however long it waits.
  const tasks = data?.approvals.filter(isPersonTask) ?? [];
  const approvals = data?.approvals.filter((a) => !isPersonTask(a)) ?? [];

  return (
    <Page>
      <LiveWorkspaces accountId={account.id} />
      <PageHeader
        eyebrow={account.name}
        title={t('Needs you')}
        description={t('What only you can do, questions from agents, tool calls and approvals waiting for you, and failed work across all workspaces. Answer right here — the agent picks it up immediately.')}
      />
      {inbox.isPending ? (
        <LoadingRows rows={4} />
      ) : inbox.isError ? (
        <ErrorState error={inbox.error} onRetry={() => void inbox.refetch()} />
      ) : !data || data.total === 0 ? (
        <EmptyState
          icon={<CheckCheck />}
          title={t('Nothing needs you right now')}
          description={t('When an agent has a question, needs an approval or something fails, it shows up here.')}
        />
      ) : (
        <div className="flex flex-col gap-8">
          {tasks.length > 0 && (
            <InboxSection icon={<Hand />} title={t('For you to do')} count={tasks.length}>
              {tasks.map((a) => (
                <PersonTaskCard
                  key={a.id}
                  approval={a}
                  workspaceId={a.workspaceId}
                  desk={{ id: a.deskId, name: a.deskName }}
                  onDecided={() => remove('approvals', a.id)}
                />
              ))}
            </InboxSection>
          )}
          {data.questions.length > 0 && (
            <InboxSection icon={<MessageCircleQuestion />} title={t('Questions from agents')} count={data.questions.length}>
              {data.questions.map((q) => (
                <QuestionCard key={q.workItem.id} item={q} accountId={account.id} />
              ))}
            </InboxSection>
          )}
          {data.actions.length > 0 && (
            <InboxSection icon={<ShieldAlert />} title={t('Tool calls to approve')} count={groupActions(data.actions).length}>
              {groupActions(data.actions).map((group) => (
                <ActionGroupCard key={group[0].groupId} actions={group} onDecided={() => remove('actions', group[0].groupId)} />
              ))}
            </InboxSection>
          )}
          {approvals.length > 0 && (
            <InboxSection icon={<ShieldAlert />} title={t('Approvals')} count={approvals.length}>
              {approvals.map((a) => (
                <ApprovalCard key={a.id} approval={a} accountId={account.id} />
              ))}
            </InboxSection>
          )}
          {data.failed.length > 0 && (
            <InboxSection icon={<AlertTriangle />} title={t('Failed')} count={data.failed.length}>
              {data.failed.map((w) => (
                <FailedCard key={w.id} item={w} accountId={account.id} />
              ))}
            </InboxSection>
          )}
        </div>
      )}
    </Page>
  );
}

function InboxSection({ icon, title, count, children }: { icon: ReactNode; title: string; count: number; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold [&_svg]:size-4 [&_svg]:text-muted-foreground">
        {icon}
        {title}
        <span className="rounded-full bg-muted px-2 text-xs font-medium text-muted-foreground tabular-nums">{count}</span>
      </h2>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

function ItemHeader({ deskId, deskName, workspaceId, workItemId, title }: { deskId: string; deskName?: string | null; workspaceId: string; workItemId: string; title: string }) {
  return (
    <div className="min-w-0">
      <Link
        to="/w/$workspaceId/desks/$deskId"
        params={{ workspaceId, deskId }}
        className="text-xs text-muted-foreground hover:text-foreground hover:underline"
      >
        {deskName ?? t('Desk')}
      </Link>
      <Link
        to="/w/$workspaceId/work-items/$workItemId"
        params={{ workspaceId, workItemId }}
        className="block truncate text-sm font-semibold hover:text-primary hover:underline"
      >
        {title}
      </Link>
    </div>
  );
}

function QuestionCard({ item, accountId }: { item: InboxQuestion; accountId: string }) {
  const remove = useOptimisticRemove(accountId);
  const toast = useToast();
  const w = item.workItem;
  return (
    <Card className="border-amber-300 p-4 dark:border-amber-900">
      <div className="flex items-start justify-between gap-3">
        <ItemHeader deskId={w.deskId} deskName={w.deskName} workspaceId={w.workspaceId} workItemId={w.id} title={w.title} />
        <RelativeTime value={item.askedAt ?? w.updatedAt} className="shrink-0 text-xs text-muted-foreground" />
      </div>
      <div className="mt-3 flex gap-2.5">
        <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-indigo-500/15 text-indigo-600 dark:text-indigo-300">
          <Bot className="size-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 text-xs font-medium text-foreground/80">{t('{agent} asks:', { agent: w.assignedAgentName ?? t('Agent') })}</div>
          <div className="rounded-2xl rounded-tl-sm border bg-card px-3.5 py-2.5">
            {item.question?.trim() ? (
              <Markdown text={item.question} />
            ) : (
              <p className="text-sm text-muted-foreground">{w.waitingReason || t('The agent is waiting for information from you.')}</p>
            )}
          </div>
        </div>
      </div>
      {item.options.length > 0 && (
        <QuestionOptions
          className="mt-3"
          workItemId={w.id}
          options={item.options}
          onAnswered={() => {
            remove('questions', w.id);
            toast.success(t('Answer sent'), w.assignedAgentName ? t('{agent} picks it up now.', { agent: w.assignedAgentName }) : t('The agent picks it up now.'));
          }}
        />
      )}
      <AnswerBox
        className="mt-3"
        workItemId={w.id}
        onSent={() => {
          remove('questions', w.id);
          toast.success(t('Answer sent'), w.assignedAgentName ? t('{agent} picks it up now.', { agent: w.assignedAgentName }) : t('The agent picks it up now.'));
        }}
      />
    </Card>
  );
}

function payloadSummary(payload: Record<string, unknown>): string | null {
  if (isEmptyObject(payload)) return null;
  return Object.entries(payload)
    .slice(0, 4)
    .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`.slice(0, 120))
    .join(' · ');
}

function ApprovalCard({ approval: a, accountId }: { approval: InboxApproval; accountId: string }) {
  const remove = useOptimisticRemove(accountId);
  const toast = useToast();
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState<'approve' | 'reject' | 'pick' | null>(null);
  const [picking, setPicking] = useState<string | null>(null);
  const start = a.type === START_WORK_APPROVAL;
  const summary = start ? null : payloadSummary(a.payload);
  const options = a.options ?? [];

  const resolve = async (kind: 'approve' | 'reject' | 'pick', choice?: string) => {
    setBusy(kind);
    setPicking(choice ?? null);
    try {
      const body = { comment: comment.trim() || undefined, choice };
      if (kind === 'reject') await api.approvals.reject(a.id, body);
      else await api.approvals.approve(a.id, body);
      remove('approvals', a.id);
      toast.success(
        start
          ? kind === 'approve' ? t('Approved — it starts when it’s its turn.') : t('Rejected — it won’t start.')
          : kind === 'pick'
            ? t('Decided — the agent continues.')
            : kind === 'approve' ? t('Approved — the agent continues.') : t('Rejected — the agent will be told.'),
      );
    } catch (err) {
      toast.error(t('That didn’t work'), errorMessage(err));
    } finally {
      setBusy(null);
      setPicking(null);
    }
  };

  return (
    <Card className="border-orange-300 p-4 dark:border-orange-800">
      <div className="flex items-start justify-between gap-3">
        <ItemHeader deskId={a.deskId} deskName={a.deskName} workspaceId={a.workspaceId} workItemId={a.workItemId} title={a.workItemTitle} />
        <RelativeTime value={a.requestedAt} className="shrink-0 text-xs text-muted-foreground" />
      </div>
      {a.description ? <Markdown text={a.description} className="mt-2" /> : <p className="mt-2 text-sm">{t('No description given.')}</p>}
      {summary && <p className="mt-1.5 truncate font-mono text-xs text-muted-foreground" title={summary}>{summary}</p>}
      {options.length > 0 && (
        <DecisionOptions className="mt-3" options={options} picking={picking} disabled={!!busy} onPick={(choice) => void resolve('pick', choice)} />
      )}
      <Textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={options.length ? t('Or answer in your own words (optional)') : t('Comment for the agent (optional)')}
        rows={1}
        className="mt-3 min-h-9 bg-card"
      />
      <div className="mt-2 flex flex-wrap gap-2">
        {options.length ? (
          <Button size="sm" variant="outline" loading={busy === 'approve'} disabled={!!busy || !comment.trim()} onClick={() => void resolve('approve')}>
            {busy !== 'approve' && <Check />} {t('Send my answer')}
          </Button>
        ) : (
          <Button
            size="sm"
            className="bg-emerald-600 text-white hover:bg-emerald-600/90"
            loading={busy === 'approve'}
            disabled={!!busy}
            onClick={() => void resolve('approve')}
          >
            {busy !== 'approve' && <Check />} {t('Approve')}
          </Button>
        )}
        <Button size="sm" variant="outline" loading={busy === 'reject'} disabled={!!busy} onClick={() => void resolve('reject')}>
          {busy !== 'reject' && <X />} {t('Reject')}
        </Button>
      </div>
    </Card>
  );
}

function FailedCard({ item: w, accountId }: { item: WorkItem; accountId: string }) {
  const remove = useOptimisticRemove(accountId);
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // Reason: the latest failure line from the activity (falls back to the result).
  const activity = useQuery({
    queryKey: qk.workItemActivity(w.id),
    queryFn: () => api.workItems.activity(w.id),
    staleTime: 60_000,
  });
  const failure = [...(activity.data ?? [])].reverse().find((e) => /fail|error|lost/i.test(e.type));
  const reason = failure?.message || w.result || null;

  const retry = async () => {
    setBusy(true);
    try {
      await api.workItems.retry(w.id);
      remove('failed', w.id);
      toast.success(t('Trying again'));
    } catch (err) {
      toast.error(t('That didn’t work'), errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-red-200 p-4 dark:border-red-900">
      <div className="flex items-start justify-between gap-3">
        <ItemHeader deskId={w.deskId} deskName={w.deskName} workspaceId={w.workspaceId} workItemId={w.id} title={w.title} />
        <RelativeTime value={w.updatedAt} className="shrink-0 text-xs text-muted-foreground" />
      </div>
      <p className="mt-2 line-clamp-3 text-sm break-words text-red-800 dark:text-red-300">
        {reason ?? (activity.isPending ? '…' : t('No reason recorded.'))}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={() => void retry()} loading={busy}>
          {!busy && <RotateCcw />} {t('Retry')}
        </Button>
        <Button asChild size="sm" variant="outline">
          <Link to="/w/$workspaceId/work-items/$workItemId" params={{ workspaceId: w.workspaceId, workItemId: w.id }}>
            {t('Open Work Item')}
          </Link>
        </Button>
      </div>
    </Card>
  );
}
